import { useState, useContext } from "react";
import { AuthContext } from "../../context/AuthContext";
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Divider,
  DialogActions,
  Button,
  useMediaQuery,
  useTheme
} from "@mui/material";

// Helper function to correctly force UTC to IST conversion
const formatOrderTime = (dateStr) => {
  if (!dateStr) return "N/A";

  // Ensure string ends with 'Z' so JS knows it is UTC
  const utcStr = dateStr.endsWith("Z") ? dateStr : `${dateStr}Z`;

  return new Date(utcStr).toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    hour12: true,
  });
};

export default function AllOrders({ order, onViewItems }) {
  const { admin } = useContext(AuthContext); // Fetch admin role

  const [open, setOpen] = useState(false);
  const [reasonOpen, setReasonOpen] = useState(false); // 👇 NEW: State for Reason Dialog
  const [orderDetails] = useState(null);

  const theme = useTheme();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down("sm"));

  // Modern color mapping based on status
  const getModernStatusStyle = (status) => {
    switch (status) {
      case "DELIVERED": return { bg: "#DDECC8", text: "#577A34" };
      case "CANCELLED":
      case "REJECTED": return { bg: "#FFF0EE", text: "#E85D3F" };
      default: return { bg: "#FFF6DE", text: "#F29D17" };
    }
  };

  const statusStyle = getModernStatusStyle(order.status);

  return (
    <>
      <Card
        elevation={0}
        // onClick disabled for now as requested
        sx={{
          borderRadius: '24px',
          border: "none",
          bgcolor: '#FFFFFF',
          width: "100%",
          boxSizing: "border-box",
          boxShadow: '0 10px 24px rgba(0, 0, 0, 0.12), 0 4px 8px rgba(0, 0, 0, 0.06)',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            transform: 'translateY(-3px)',
            boxShadow: '0 16px 32px rgba(0, 0, 0, 0.15), 0 8px 16px rgba(0, 0, 0, 0.1)',
          }
        }}
      >
        <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={1}
          >
            {/* Left Info */}
            <Box flex={1} minWidth={0} pr={1}>
              <Box display="flex" flexDirection="column" alignItems="flex-start" mb={1}>
                {order.batch_id && (
                  <Chip
                    label={`Batch #${order.batch_id}`}
                    size="small"
                    sx={{
                      fontWeight: "800",
                      height: 22,
                      fontSize: "0.7rem",
                      mb: 1,
                      bgcolor: '#FFF6DE',
                      color: '#F29D17',
                      borderRadius: '6px'
                    }}
                  />
                )}
                <Typography variant="h6" fontWeight="900" sx={{ color: '#3A2C23', lineHeight: 1.2, wordBreak: "break-word" }}>
                  Order #{order.order_id || order.id}
                </Typography>
              </Box>

              <Typography variant="body2" sx={{ color: '#A59D96', fontWeight: 700 }} mb={0.5} noWrap>
                {formatOrderTime(order.created_at)}
              </Typography>

              <Typography variant="body2" sx={{ color: '#6E655E', fontWeight: 600 }} noWrap>
                User ID: <Typography component="span" sx={{ color: '#3A2C23', fontWeight: 800 }}>{order.user_id}</Typography>
              </Typography>
            </Box>

            {/* Right Info (Status and Payment) */}
            <Box display="flex" flexDirection="column" alignItems="flex-end" gap={1} flexShrink={0}>
              <Chip
                label={order.status}
                size="small"
                sx={{
                  fontWeight: "800",
                  fontSize: "0.7rem",
                  height: 24,
                  borderRadius: '6px',
                  backgroundColor: statusStyle.bg,
                  color: statusStyle.text
                }}
              />

              {/* 👇 NEW: Reason Button under Cancelled Status */}
              {order.status === "CANCELLED" && order.cancel_reason && (
                <Button
                  size="small"
                  onClick={() => setReasonOpen(true)}
                  sx={{
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: '900',
                    bgcolor: '#FFF0EE',
                    color: '#E85D3F',
                    px: 1.5,
                    py: 0.2,
                    minWidth: 0,
                    fontSize: '0.65rem',
                    mt: -0.5, // Pulls it slightly closer to the chip
                    border: "1px solid #F5D0C9",
                    '&:hover': { bgcolor: '#F5D0C9', borderColor: '#E85D3F' }
                  }}
                >
                  Reason
                </Button>
              )}

              {/* PAID AMOUNT HIDDEN FROM ZONAL ADMINS */}
              {admin?.role !== "ZONAL_ADMIN" && (
                <Typography variant="body2" fontWeight="900" sx={{ color: '#E85D3F', mt: 0.5 }}>
                  Paid: ₹ {order.final_amount}
                </Typography>
              )}

              {/* VIEW ITEMS BUTTON */}
              {order.items && order.items.length > 0 && (
                <Button
                  variant="contained"
                  disableElevation
                  size="small"
                  onClick={() => onViewItems && onViewItems(order)}
                  sx={{
                    borderRadius: '10px',
                    textTransform: 'none',
                    fontWeight: '900',
                    bgcolor: '#FFF6DE',
                    color: '#D97706',
                    px: 2,
                    py: 0.5,
                    minWidth: 0,
                    fontSize: '0.75rem',
                    border: "1px solid #FFE4B5",
                    '&:hover': { bgcolor: '#F5B71D', color: '#3A2C23', borderColor: '#F5B71D' }
                  }}
                >
                  View Items
                </Button>
              )}
            </Box>
          </Stack>

        </CardContent>
      </Card>

      {/* 👇 NEW: CANCELLATION REASON MODAL */}
      <Dialog
        open={reasonOpen}
        onClose={() => setReasonOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: '24px',
            bgcolor: '#FFFFFF',
            boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
            minWidth: '320px',
            textAlign: 'center',
            p: 1
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: "900", color: '#E85D3F', pb: 1 }}>
          Cancellation Reason
        </DialogTitle>
        <DialogContent>
          {order.cancelled_by && (
            <Typography variant="caption" sx={{ display: 'block', color: '#A59D96', fontWeight: 800, letterSpacing: 0.5, mb: 1 }}>
              CANCELLED BY: {order.cancelled_by.toUpperCase()}
            </Typography>
          )}
          <Typography variant="body1" sx={{ color: '#3A2C23', fontWeight: 700, fontStyle: 'italic', bgcolor: '#FFFDF8', p: 2, borderRadius: '12px', border: '1px dashed #ECE5DA' }}>
            "{order.cancel_reason}"
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button
            onClick={() => setReasonOpen(false)}
            variant="outlined"
            sx={{
              borderRadius: '12px',
              minWidth: 100,
              fontWeight: 800,
              color: '#6E655E',
              borderColor: '#ECE5DA',
              textTransform: 'none',
              px: 4,
              py: 1,
              '&:hover': { bgcolor: '#FFFDF8', borderColor: '#A59D96' }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* ORDER DETAILS MODAL (Hidden via disabled click event for now) */}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={fullScreenDialog}
        PaperProps={{
          sx: {
            borderRadius: fullScreenDialog ? 0 : '24px',
            bgcolor: '#FFFDF8',
            boxShadow: '0 24px 48px rgba(0,0,0,0.2)'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: "900", color: '#3A2C23', borderBottom: "1px dashed #ECE5DA", pb: 2, pt: 3, px: { xs: 2.5, sm: 4 } }}>
          Order Details #{order.order_id || order.id}
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2.5, sm: 4 }, pt: "24px !important", overflowX: "hidden" }}>

          {/* USER INFO BOX */}
          <Box mb={3} p={2.5} sx={{ backgroundColor: "#FFFFFF", borderRadius: '16px', border: '1px solid #ECE5DA', boxShadow: '0 4px 12px rgba(0,0,0,0.03)' }}>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Typography variant="caption" sx={{ color: '#A59D96', fontWeight: '800', letterSpacing: 0.5 }}>USER ID</Typography>
                <Typography variant="body1" fontWeight="800" sx={{ color: '#3A2C23' }}>{order.user_id}</Typography>
              </Grid>
              <Grid item xs={8}>
                <Typography variant="caption" sx={{ color: '#A59D96', fontWeight: '800', letterSpacing: 0.5 }}>NAME</Typography>
                <Typography variant="body1" fontWeight="800" sx={{ color: '#3A2C23' }}>{orderDetails?.user_name || "N/A"}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" sx={{ color: '#A59D96', fontWeight: '800', letterSpacing: 0.5 }}>ADDRESS</Typography>
                <Typography variant="body2" fontWeight="600" sx={{ color: '#6E655E', wordBreak: "break-word", lineHeight: 1.4 }}>
                  {orderDetails?.user_address || "N/A"}
                </Typography>
              </Grid>
            </Grid>
          </Box>

          <Typography variant="subtitle1" fontWeight="900" sx={{ color: '#3A2C23' }} mb={2}>
            Items Ordered
          </Typography>

          <Box display="flex" justifyContent="space-between" mb={1.5} pb={1} borderBottom="1px dashed #ECE5DA">
            <Typography variant="caption" sx={{ color: '#A59D96', fontWeight: '800', letterSpacing: 0.5, flex: 1, minWidth: 0 }}>ITEM</Typography>
            <Box display="flex" gap={1} sx={{ width: "100px", justifyContent: "flex-end", flexShrink: 0 }}>
              <Typography variant="caption" sx={{ color: '#A59D96', fontWeight: '800', letterSpacing: 0.5, width: "45px", textAlign: "right" }}>VP</Typography>
              <Typography variant="caption" sx={{ color: '#A59D96', fontWeight: '800', letterSpacing: 0.5, width: "45px", textAlign: "right" }}>SP</Typography>
            </Box>
          </Box>

          {orderDetails?.items.map((item, index) => (
            <Box key={index} display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
              <Typography variant="body2" fontWeight="700" sx={{ color: '#3A2C23', flex: 1, pr: 1, wordBreak: "break-word", minWidth: 0 }}>
                {item.name} <Typography component="span" sx={{ color: "#E85D3F" }} fontWeight="900">x{item.quantity}</Typography>
              </Typography>
              <Box display="flex" gap={1} sx={{ width: "100px", justifyContent: "flex-end", flexShrink: 0 }}>
                <Typography variant="body2" fontWeight="600" sx={{ color: '#6E655E', width: "45px", textAlign: "right" }}>₹ {item.vendor_price * item.quantity}</Typography>
                <Typography variant="body2" fontWeight="600" sx={{ color: '#6E655E', width: "45px", textAlign: "right" }}>₹ {item.selling_price * item.quantity}</Typography>
              </Box>
            </Box>
          ))}

          <Divider sx={{ my: 3, borderStyle: 'dashed', borderColor: '#ECE5DA' }} />

          {/* TOTALS */}
          <Box display="flex" flexDirection="column" gap={1.5}>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="#A59D96" fontWeight="700">Vendor Total:</Typography>
              <Typography variant="body2" fontWeight="800" sx={{ color: '#3A2C23' }}>₹ {orderDetails?.total_vendor || 0}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="#A59D96" fontWeight="700">Selling Total:</Typography>
              <Typography variant="body2" fontWeight="800" sx={{ color: '#3A2C23' }}>₹ {orderDetails?.total_selling || 0}</Typography>
            </Box>
            {/* 🚫 GST DISABLED — Mealno is not GST registered, so no GST is charged. */}
            {/*
            <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="#A59D96" fontWeight="700">GST:</Typography>
                <Typography variant="body2" fontWeight="800" sx={{ color: '#3A2C23' }}>₹ {orderDetails?.gst || 0}</Typography>
            </Box>
            */}
            <Box display="flex" justifyContent="space-between" mt={1} pt={2} borderTop="1px dashed #ECE5DA">
              <Typography variant="subtitle1" fontWeight="900" sx={{ color: '#3A2C23' }}>User Paid:</Typography>
              <Typography variant="subtitle1" fontWeight="900" sx={{ color: "#E85D3F" }}>₹ {orderDetails?.final_amount || order.final_amount}</Typography>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: { xs: 2.5, sm: 3 }, px: { sm: 4 }, borderTop: "1px solid #ECE5DA", bgcolor: '#FFFFFF', borderRadius: '0 0 24px 24px' }}>
          <Button
            onClick={() => setOpen(false)}
            variant="outlined"
            sx={{
              borderRadius: '12px',
              minWidth: 100,
              fontWeight: 800,
              color: '#6E655E',
              borderColor: '#ECE5DA',
              textTransform: 'none',
              px: 4,
              py: 1
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}